import React from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../../components/Input";
import Dropdown from "../../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { Validation } from "../../../../../interfaces/common";
import { AddEdit as AddEditInterface } from "../../../../../interfaces/property.interface";

interface AddressProps {
  countries: any[];
  states: any[];
  allCountries: any[];
  allStates: any[];
  allCities: any[];
  cities: any[];
  formData: AddEditInterface;
  validation: Validation;
  view?: boolean;
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
}

const Address: React.FC<AddressProps> = ({
  countries,
  states,
  allCountries,
  allStates,
  allCities,
  cities,
  formData,
  validation,
  view,
  dropdownSearchData,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="col-md-6">
        <div className="form-floating form-floating-outline">
          <Input
            type="text"
            required
            id={VALIDATION_FIELD_MAPPING.address1}
            name={VALIDATION_FIELD_MAPPING.address1}
            disabled={view}
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.address1)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.address1)?.message ?? ''}
            label={t("property.addEdit.pageTexts.form.address1.label")}
            placeholder={t("property.addEdit.pageTexts.form.address1.placeholder")}
            value={formData.address1}
            onKeyDown={onKeyDown}
            onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.address1)}
            onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.address1)}
          />
        </div>
      </div>
      <div className="col-md-6">
        <div className="form-floating form-floating-outline">
          <Input
            type="text"
            id={VALIDATION_FIELD_MAPPING.address2}
            name={VALIDATION_FIELD_MAPPING.address2}
            disabled={view}
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.address2)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.address2)?.message ?? ''}
            label={t("property.addEdit.pageTexts.form.address2.label")}
            placeholder={t("property.addEdit.pageTexts.form.address2.placeholder")}
            value={formData.address2}
            onKeyDown={onKeyDown}
            onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.address2)}
          />
        </div>
      </div>

      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.country ? [formData.country] : []}
            options={(countries || []).map((country: any) => ({
              label: country.name,
              value: country.id
            }))}
            allOptions={(allCountries || []).map((country: any) => ({
              label: country.name,
              value: country.id
            }))}
            id={VALIDATION_FIELD_MAPPING.country}
            name={VALIDATION_FIELD_MAPPING.country}
            disabled={view}
            searchText={dropdownSearchData.country}
            onSearch={onDropdownSearchChange}
            placeholder={t("property.addEdit.pageTexts.form.country.placeholder")}
            label={t("property.addEdit.pageTexts.form.country.label")}
            required
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.country)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.country)?.message ?? ''}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.state ? [formData.state] : []}
            options={(states || []).map((state: any) => ({
              label: state.name,
              value: state.id
            }))}
            allOptions={(allStates || []).map((state: any) => ({
              label: state.name,
              value: state.id
            }))}
            id={VALIDATION_FIELD_MAPPING.state}
            name={VALIDATION_FIELD_MAPPING.state}
            disabled={view}
            searchText={dropdownSearchData.state}
            onSearch={onDropdownSearchChange}
            placeholder={t("property.addEdit.pageTexts.form.state.placeholder")}
            label={t("property.addEdit.pageTexts.form.state.label")}
            required
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.state)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.state)?.message ?? ''}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.city ? [formData.city] : []}
            options={(cities || []).map(city => ({
              label: city.name,
              value: city.id
            }))}
            allOptions={(allCities || []).map(city => ({
              label: city.name,
              value: city.id
            }))}
            id={VALIDATION_FIELD_MAPPING.city}
            name={VALIDATION_FIELD_MAPPING.city}
            searchText={dropdownSearchData.city}
            onSearch={onDropdownSearchChange}
            label={t("property.addEdit.pageTexts.form.city.label")}
            placeholder={t("property.addEdit.pageTexts.form.city.placeholder")}
            required
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.city)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.city)?.message ?? ''}
            onChange={onInputChange}
            onBlur={onInputBlur}
            disabled={!!view}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Input
            type="text"
            required
            id={VALIDATION_FIELD_MAPPING.postal}
            name={VALIDATION_FIELD_MAPPING.postal}
            disabled={view}
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.postal)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.postal)?.message ?? ''}
            label={t("property.addEdit.pageTexts.form.postal.label")}
            placeholder={t("property.addEdit.pageTexts.form.postal.placeholder")}
            value={formData.postal}
            onKeyDown={onKeyDown}
            onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.postal)}
            onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.postal)}
          />
        </div>
      </div>
    </>
  );
};

export default Address;